import {Box, Text, useDisclosure, VStack} from '@chakra-ui/react';
import {useQuery} from '@tanstack/react-query';
import {getOrderById, retryTransaction} from 'api/requests';
import {AsideLayout} from 'components/layouts';
import {OrderAddress} from 'components/layouts/aside-layout';
import {CourierDataInfo, OrderItemList, OrderReview, OrderStatus, OrderSummary, Support} from 'components/pages/order';
import {OrderForAnother} from 'components/pages/order/OrderForAnother';
import {useCustomerContext} from 'components/providers';
import {Head, OrderProviderSummary, OrderTotalDetails} from 'components/shared';
import {formatDatewith30Min, useAuth, useFormDate, useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {FC, useEffect, useState} from 'react';
import {IOrder, OrderStatusEnum, OrderStatusEnumCourierExpress} from 'types';
import {getDeliveryTimeString} from 'utils/helpers';

import {MaibPaymentModal} from '../MaibPayModal';
import {OrderInstructions} from '../OrderInstructions';

export const Order: FC = () => {
  const [stopRefetchOrder, setStopRefetchOrder] = useState(false);
  const {query, locale, push} = useRouter();
  const {t} = useTranslation();
  const {nonAuthRedirectTo} = useAuth();
  const {isLoadingGetting} = useCustomerContext();
  const [retryTransactionDisabled, setretryTransactionDisabled] = useState(false);

  const [maibUrl, setMaibUrl] = useState('');
  const {onOpen, isOpen, onClose} = useDisclosure();

  const {data: order, isLoading} = useQuery<IOrder>({
    queryKey: ['ORDER', query.orderId, locale],
    queryFn: () => getOrderById(`${query.orderId}`),
    enabled: !stopRefetchOrder && !isLoadingGetting,
    refetchInterval: 30000,
    staleTime: 0,
  });

  useEffect(() => {
    nonAuthRedirectTo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    address,
    money,
    items,
    provider,
    status,
    short_uuid,
    datetime_to_deliver,
    delivered,
    preorder,
    is_canceled,
    courier,
  } = order ?? ({} as IOrder);

  const {distanceDay, date: preorderDate} = useFormDate(preorder ?? '', 'dd.MM.yyyy');

  const isDelivered = [OrderStatusEnum.DELIVERED, OrderStatusEnumCourierExpress.DELIVERED].includes(status?.code);
  const isDelivering = [
    OrderStatusEnum.PICKED,
    OrderStatusEnum.DELIVERING,
    OrderStatusEnum.DELIVERING_CLIENT,
    OrderStatusEnum.AT_CLIENT,
    OrderStatusEnum.AT_CLIENT_PART1,

    OrderStatusEnumCourierExpress.PICKED,
    OrderStatusEnumCourierExpress.DELIVERING,
    OrderStatusEnumCourierExpress.AT_CLIENT,
    OrderStatusEnumCourierExpress.AT_CLIENT_PART1,
  ].includes(status?.code);

  useEffect(() => {
    if (isDelivered || (is_canceled && !stopRefetchOrder)) {
      setStopRefetchOrder(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDelivered, is_canceled]);
  const isStrausDelivering = order?.provider.straus_delivering;
  const deliveryTime = getDeliveryTimeString(provider?.avg_delivery_time);
  const time = preorder ? `${formatDatewith30Min(preorder)}` : order && deliveryTime;

  const deliveryTimeTitle = distanceDay.isTomorrowDate
    ? `${t('TOMORROW_IN')} ${time}`
    : distanceDay.isToday
    ? `${t('TODAY_IN')} ${time}`
    : `${time}`;

  const onHelperOnlinePayment = () => {
    if (!order?.uuid) return;

    if (order.transaction_pay_link) {
      if (order?.redirect) return push(order.transaction_pay_link); //mia validation
      // if (isVitiok) {
      setMaibUrl(order.transaction_pay_link);
      return onOpen();
      // }
      // return push(order.transaction_pay_link);
    }
  };

  const onRetryOnlinePayment = () => {
    if (!order?.uuid) return;

    if (order.transaction_pay_link) {
      if (order?.redirect) return push(order.transaction_pay_link); //mia validation
      // if (isVitiok) {
      setMaibUrl(order.transaction_pay_link);
      return onOpen();
      // }
      // return push(order.transaction_pay_link);
    }

    setretryTransactionDisabled(true);
    retryTransaction(order.uuid)
      .then((res) => {
        if (res?.url) {
          // if (isVitiok) {
          setMaibUrl(res.url);
          onOpen();
          return;
          // }
          // return push(res.url);
        }
      })
      .finally(() => {
        setretryTransactionDisabled(false);
      });
  };

  return (
    <AsideLayout
      showLogo
      aside={
        <Box mb="30px">
          {provider && (
            <OrderTotalDetails
              t={t}
              cartItemsPrice={money?.products_cost + (order?.money?.promo_code_reduction ?? 0)}
              deliveryDetails={{
                deliveryAmount: money?.delivery_cost - (order?.nighttime_added_price ?? 0),
              }}
              deliveryTime={deliveryTimeTitle}
              hasPreorder={!!preorderDate}
              voucher={{
                promoCode: order?.money?.promo_code,
                reduction: order?.money?.promo_code_reduction,
                is_valid: !!order?.money?.promo_code || !!order?.money?.promo_code_reduction,
              }}
              dynamicDeliveryPrice={{
                surcharge: 0,
                distance_to_client: 0,
                surcharge_for_out_area: 0,
              }}
              nighttimeAddedTerms={provider?.base_delivery_cost_data?.nighttime_added_terms}
              orderNighttime_added_price={order?.nighttime_added_price}
              order={order}
              isOrderCreated={!!order?.created}
            />
          )}
        </Box>
      }
    >
      <Head title={t('ORDER_DETAILS')} />
      {isLoading && <Text>{t('ORDER_LOADING')}...</Text>}

      {order && (
        <VStack spacing="30px" alignItems="stretch">
          {provider && (
            <OrderProviderSummary
              t={t}
              hasPreorder={!!preorderDate}
              provider={{
                logo_thumbnail: provider.logo_thumbnail,
                slug: provider.slug,
                name: provider.name,
                ratings: provider.ratings?.average.toFixed(2),
                avg_delivery_time: preorder ? undefined : provider.avg_delivery_time,
                delivery_price: money.delivery_cost,
                online_payment: provider.online_payment,
              }}
            />
          )}
          {isDelivered && !order?.ratings.order_rate ? (
            <OrderReview order={order} t={t} />
          ) : (
            <OrderStatus
              t={t}
              short_uuid={short_uuid}
              providerImg={provider && provider.showcase_image_thumbnail}
              order={order}
              datetime_to_deliver={datetime_to_deliver}
              onHelperOnlinePayment={onHelperOnlinePayment}
              onRetryOnlinePayment={onRetryOnlinePayment}
              retryTransactionDisabled={retryTransactionDisabled}
              isDelivering={isDelivering}
            />
          )}
          {isDelivering && order?.courier && <CourierDataInfo courier={courier} t={t} />}
          {isDelivered && isStrausDelivering && <OrderSummary short_uuid={short_uuid} delivered={delivered} t={t} />}
          {!isDelivered && <Support />}
          <OrderAddress address={address} />
          {order?.another_person && <OrderForAnother anotherPerson={order.another_person} />}
          {order?.instructions && <OrderInstructions instructions={order?.instructions} />}
          <OrderItemList t={t} items={items} />
        </VStack>
      )}
      <MaibPaymentModal isOpen={isOpen} url={maibUrl} uuid={order?.uuid ?? ''} onClose={onClose} />
    </AsideLayout>
  );
};
